import { useMediaQuery, useTheme } from '@mui/material';
import React from 'react';

import { SettingsPageHeaderTitle } from 'src/legacy/components/Settings/SettingsPageHeaderTitle';
import type { RouteAccess } from 'src/routes/routeAccess';

import { AccessGate } from '../AccessGate';
import { useShell } from '../AppShell';
import { useHeaderSlot } from '../AppShell/HeaderSlotContext';

import { DefaultVariant } from './variants/DefaultVariant';
import { FullbleedVariant } from './variants/FullbleedVariant';
import { SettingsColumnVariant } from './variants/SettingsColumnVariant';
import { SettingsVariant } from './variants/SettingsVariant';

export type PageShellVariant =
  | 'default'
  | 'fullbleed'
  | 'settings'
  /** The `new-settings` layout: a 760px column under the registry page header. */
  | 'settingsColumn';

// Hoisted to module scope so the JSX element is referentially stable across
// renders, preventing the useEffect in PageShell from firing every render.
const DEFAULT_SETTINGS_HEADER = <SettingsPageHeaderTitle showMenu />;

export interface PageShellProps {
  children: React.ReactNode;
  /** Custom header content injected into AppShell's AppBar via HeaderSlotContext. */
  header?: React.ReactNode;
  /** Toolbar rendered above the page content (below the AppBar). */
  toolbar?: React.ReactNode;
  /** Layout variant. Defaults to 'default'. */
  variant?: PageShellVariant;
  /** Permission key checked by AccessGate. When omitted, no access check is performed. */
  access?: keyof RouteAccess;
  /** Allow unauthenticated access (forwarded to AccessGate). */
  unauthAccess?: boolean;
  /** Whether to apply horizontal gutters/spacing. Defaults to true. */
  showSpace?: boolean;
  /** Optional max-width constraint for the content area. */
  maxWidth?: string;
  /** When true, removes default header padding in AppShell's AppBar. */
  ignoreHeaderPadding?: boolean;
  /** Whether to show the sidebar. Defaults to true. */
  showSidebar?: boolean;
  /** When true, hides the default title/description area in the settings variant.
   *  Use for pages that render their own title (e.g. via ModuleContainer). */
  hideSettingsDescription?: boolean;
  /** When true, shows the context bar on the right side of the layout */
  showContextBar?: boolean;
  /** When true, keeps AppShell on the fixed-width sidebar layout. */
  disableResizableSidebar?: boolean;
  /**
   * Minimum width in pixels for the main content area when the context bar is
   * open. When the panel is narrower than this value, horizontal scrolling
   * appears.
   */
  contextBarContentMinWidth?: number;
  /**
   * Portals page content through AppShell so React state survives the resizable
   * ↔ legacy nav layout swap at ~960px. Opt in per page until rolled out broadly.
   */
  portalPageContent?: boolean;
  /** `settingsColumn` only: the page draws its own title and actions. */
  hideSettingsColumnHeader?: boolean;
}

export const PageShell: React.FC<PageShellProps> = ({
  children,
  header,
  toolbar,
  variant = 'default',
  access,
  unauthAccess,
  showSpace = true,
  maxWidth,
  ignoreHeaderPadding = false,
  showSidebar = true,
  hideSettingsDescription = false,
  showContextBar = false,
  disableResizableSidebar = false,
  contextBarContentMinWidth,
  portalPageContent = false,
  hideSettingsColumnHeader = false,
}) => {
  const { setHeader, clearHeader, setHeaderPadding, setShowSidebar } =
    useHeaderSlot();
  const {
    setShowContextBar,
    setContextBarContentMinWidth,
    setDisableResizableSidebar,
    setPortalPageContent,
  } = useShell();

  // For fullbleed, force header to null to hide AppBar content.
  // For settings, show the mobile-only back toggle (resizable sidebar is disabled on settings pages).
  // `header` omitted (undefined) takes the settings default; passing an explicit
  // null opts out of the app bar entirely, which `??` would have swallowed.
  const theme = useTheme();
  const isBelowMd = useMediaQuery(theme.breakpoints.down('sm'));
  // The settings column drops the top bar, except below md where it holds the
  // menu button, the only way to open the settings sidebar there.
  const settingsColumnHeader = isBelowMd ? DEFAULT_SETTINGS_HEADER : null;
  const resolvedHeader =
    variant === 'fullbleed'
      ? null
      : variant === 'settings' && header === undefined
        ? DEFAULT_SETTINGS_HEADER
        : variant === 'settingsColumn' && header === undefined
          ? settingsColumnHeader
          : header;

  const isSettingsVariant =
    variant === 'settings' || variant === 'settingsColumn';
  const shouldDisableResizableSidebar =
    isSettingsVariant || disableResizableSidebar;

  // Set the header slot on mount / when header prop changes, clear on unmount.
  React.useEffect(() => {
    if (resolvedHeader !== undefined) {
      setHeader(resolvedHeader);
    }

    setHeaderPadding(ignoreHeaderPadding);
    setShowSidebar(showSidebar);
    setShowContextBar(showContextBar);
    setContextBarContentMinWidth(contextBarContentMinWidth);
    setDisableResizableSidebar(shouldDisableResizableSidebar);
    setPortalPageContent(portalPageContent);
    return () => {
      clearHeader();
      setHeaderPadding(false);
      setShowSidebar(true);
      setShowContextBar(false);
      setContextBarContentMinWidth(undefined);
      setDisableResizableSidebar(false);
      // Do not reset portalPageContent here. Clearing it while AppShell is
      // switching from inline children to the portal host remounts PageShell,
      // which re-enters this cleanup and causes an infinite render loop.
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    resolvedHeader,
    ignoreHeaderPadding,
    showSidebar,
    showContextBar,
    contextBarContentMinWidth,
    shouldDisableResizableSidebar,
    portalPageContent,
  ]);

  const renderVariant = () => {
    switch (variant) {
      case 'fullbleed':
        return <FullbleedVariant>{children}</FullbleedVariant>;
      case 'settingsColumn':
        return (
          <SettingsColumnVariant hideColumnHeader={hideSettingsColumnHeader}>
            {children}
          </SettingsColumnVariant>
        );
      case 'settings':
        return (
          <SettingsVariant hideDescription={hideSettingsDescription}>
            {children}
          </SettingsVariant>
        );
      case 'default':
      default:
        return (
          <DefaultVariant showSpace={showSpace} maxWidth={maxWidth}>
            {children}
          </DefaultVariant>
        );
    }
  };

  const content = (
    <>
      {toolbar}
      {renderVariant()}
    </>
  );

  if (access !== undefined) {
    return (
      <AccessGate access={access} unauthAccess={unauthAccess}>
        {content}
      </AccessGate>
    );
  }

  return content;
};
